import { Link } from "@material-ui/core";
import { WarningOutlined } from "@material-ui/icons";
import { TextareaAutosize, ToggleButton, ToggleButtonGroup } from "@mui/material";
import axios from "axios";
import moment from "moment";
import { useEffect, useMemo, useState, useRef } from "react";
import { Badge, Button, CloseButton, Dropdown } from "react-bootstrap";
import { DropdownSubmenu } from "react-bootstrap-submenu";
import Modal from 'react-bootstrap/Modal';
import { GrDocumentText } from "react-icons/gr";
import { useDispatch, useSelector } from "react-redux";
import ReactSelect from "react-select";
import { fetchRequestStatuses, initLenderRequest } from "../../../../redux/actions/lenderRequestActions";
import { getCannedRestrictionsByProject } from "../../../../redux/actions/restrictionActions";
import { isAdminRole, isInternal, isLenderRole, isProjectReadOnlyRole, isProjectRole, isProjectStandardRole, isReadonlyRole, isTPORole } from "../../../../redux/actions/userAction";
import { getEnvName } from "../../../shared/constants/environment";
import { FM_STATUSES } from "../../../shared/constants/FMStatuses";
import { LINE_ITEM_STATUS, REQUEST_NOTES, REQUEST_STATUSES, isReviewableStatus, isLenderActionableStatus, isClosedStatus } from "../../../shared/constants/RequestStatuses";
import { REQUEST_TYPES } from "../../../shared/constants/RequestTypes";
import { downloadFile, getAdditionalDocuments } from "../../../shared/services/LenderRequestService";
import { getGoogleAddressFields } from "../../../shared/services/SharedService";
import DatePicker from "../../../SharedComponents/DatePicker/DatePicker.jsx";
import ReasonCodeTooltip from "../../../SharedComponents/ReasonCodeTooltip/ReasonCodeTooltip";
import EditableRequestField from "./EditableRequestField";

import "./RequestDetailsModal.css";
import { DelayFor } from "../../../shared/utilities/promise-utils.js";

// coerce "true"/"false" back to booleans
function parseBoolean(value) {
  return value === "false"
    ? false
    : value === "true"
    ? true
    : value;
}

// The canned restrictions all have names with the pattern "Restriction 1", "Restriction 2", "Restriction 3", etc.
// We want to sort them numerically instead of alphabetically.
const restrictionComparer = (rest1, rest2) => {
  const getRestrictionNumber = restriction => {
    const name = restriction?.restrictionName ?? restriction?.restrictionsName ?? restriction?.referenceName ?? "";
    const match = /Restriction (\d+)/.exec(name)?.[1];
    
    return match && !restriction.customIndex 
      ? +match 
      : Number.MAX_VALUE;
  };

  return rest1.customIndex && !rest2.customIndex ? 1
        : !rest1.customIndex && rest2.customIndex ? -1
        : getRestrictionNumber(rest1) > getRestrictionNumber(rest2) ? 1
        : getRestrictionNumber(rest1) < getRestrictionNumber(rest2) ? -1
    : (rest1?.restrictionName ?? rest1?.restrictionsName ?? rest1?.referenceName ?? "") > (rest2?.restrictionName ?? rest2?.restrictionsName ?? rest2?.referenceName ?? "") ? 1
    : (rest1?.restrictionName ?? rest1?.restrictionsName ?? rest1?.referenceName ?? "") < (rest2?.restrictionName ?? rest2?.restrictionsName ?? rest2?.referenceName ?? "") ? -1
        : 0;
}

const RequestDetailsModal = ({ show, onClose, request, projectInfo, activityFeed }) => {
  // Derive Status Note display text from the most recent activity event
  const mostRecentEvent = activityFeed?.[0];
  const shouldShowExternalNote = !isInternal() && !isProjectReadOnlyRole() && mostRecentEvent?.notesIsExternal;
  
  let statusNoteText = "(none)";
  if (isInternal() || isProjectReadOnlyRole()) {
    // Internal/ProjectReadOnly users: always show the raw note text
    const rawNote = mostRecentEvent?.notes?.trim() || "";
    statusNoteText = rawNote || "(none)";
  } else if (shouldShowExternalNote) {
    // External users: apply substitution rule for "Additional Information Received"
    if (mostRecentEvent.internalStatus === REQUEST_STATUSES.ADDITIONAL_INFORMATION_RECEIVED) {
      statusNoteText = REQUEST_NOTES.ADDITIONAL_DOCUMENTS_SUBMITTED;
    } else {
      statusNoteText = mostRecentEvent.notes?.trim() || "(none)";
    }
  }

  const [internalStatusName, setInternalStatusName] = useState("");
  const [statusName, setStatusName] = useState("");
  const [stagingChanges, setStagingChanges] = useState();
  const [originalChanges, setOriginalChanges] = useState();
  const [addedPhases, setAddedPhases] = useState();
  const [originalAddedPhases, setOriginalAddedPhases] = useState();
  const [documents, setDocuments] = useState([]);
  const [note, setNote] = useState("");
  const [notesExternal, setNotesExternal] = useState(false);
  const [notesBoxActive, setNotesBoxActive] = useState(false);
  const [requestedDocuments, setRequestedDocuments] = useState([]);
  const [lineItemApprovals, setLineItemApprovals] = useState(new Map());
  const [additionalDocuments, setAdditionalDocuments] = useState([]);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  
  const [availableFnmaStatuses, setAvailableFnmaStatuses] = useState([]);
  const [newFNMAStatus, setNewFNMAStatus] = useState("");
  const [showFnmaStatusDropdown, setShowFnmaStatusDropdown] = useState(false);
  const [approvalExpiryDate, setApprovalExpiryDate] = useState(null);
  const [suspensionReason, setSuspensionReason] = useState("");
  const [sellingServicingGuideRef, setSellingServicingGuideRef] = useState("");
  const [reviewedProjectExhibits, setReviewedProjectExhibits] = useState("");
  const [reasonForIneligibility, setReasonForIneligibility] = useState("");
  
  const [restrictions, setRestrictions] = useState([]);
  const [addedRestrictions, setAddedRestrictions] = useState([]);
  const [canAddRestrictions, setCanAddRestrictions] = useState(false);

  const [allReasonCodes, setAllReasonCodes] = useState([]);
  const [reasonCodeOptions, setReasonCodeOptions] = useState([]);
  const [addedReasonCodes, setAddedReasonCodes] = useState(request.reasonCodeDTOList || []);
  const [reasonCodeIds, setReasonCodeIds] = useState(new Set());
  const [canAddReasonCodes, setCanAddReasonCodes] = useState(false);
  const [shouldShowReasonCodes, setShouldShowReasonCodes] = useState(false);
  const [activeReasonCodes, setActiveReasonCodes] = useState([]); // Active reason codes from project's FM status

  const [hasPersDecision, setHasPersDecision] = useState(false);
  const [isLoadingStatusChangeData, setIsLoadingStatusChangeData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [canReview, setCanReview] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [editingItems, setEditingItems] = useState(new Set());
  const [validationErrors, setValidationErrors] = useState(new Map());
  const [phaseEditingItems, setPhaseEditingItems] = useState(new Map());
  const [phaseValidationErrors, setPhaseValidationErrors] = useState(new Map());
  const [phaseName, setPhaseName] = useState();
  const [phaseId, setPhaseId] = useState();
  const baselineStatusRef = useRef("");

  // todo: eliminate phaseLineItemApprovals and handlePhaseLineItemDecision since we are
  // setting Accept/Reject/Modify status on the entire phase
  const [phaseLineItemApprovals, setPhaseLineItemApprovals] = useState(new Map());
  
  // TODO: select project or phase field defs during initLenderRequest based on request type
  const projectFieldsMap = useSelector(state => state.lenderRequestReducer.projectFieldsMap);  
  const phaseFieldsMap = useSelector(state => state.lenderRequestReducer.phaseFieldsMap);  
  const requestStatuses = useSelector(state => state.lenderRequestReducer.requestStatuses);
  const restrictionsByProjectId = useSelector(state => state.restrictionReducer.restrictionsByProjectId);
  const allDocTypesById = useSelector(state => state.lenderRequestReducer.allDocTypesById);

  const dispatch = useDispatch();

  // Resolves the internal-facing display label for a given backend statusName
  // by looking it up in the requestStatuses list from the API.
  // Falls back to the provided fallback value (e.g. internalStatusName) or empty string.
  const getInternalDisplayStatus = (status, fallback) => {
    if (status) {
      const match = requestStatuses?.find(s => s.statusName === status);
      if (match?.internalStatus) return match.internalStatus;
      return status;
    }
    return fallback || "";
  };

  // Refs for Google Autocomplete
  const streetInputRef = useRef(null);
  const cityInputRef = useRef(null);
  const zip5InputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const cityAutocompleteRef = useRef(null);
  const zip5AutocompleteRef = useRef(null);
  const phaseStreetRefs = useRef({});
  const phaseAutocompleteRefs = useRef({});

  // Initialize Google Autocomplete for street field in Edit Project Requests
  useEffect(() => {
    const isEditing = editingItems.has("street");

    if (!isEditing || !streetInputRef?.current) {
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      return;
    }

    if (!window.google?.maps?.places) {
      console.warn('Google Maps Places API not loaded');
      return;
    }

    try {
      const autocomplete = new window.google.maps.places.Autocomplete(streetInputRef.current, {
        componentRestrictions: { country: 'US' },
        fields: ["address_components"],
        types: ['address'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place?.address_components) return;
        const addr = getGoogleAddressFields(place);
        handleAddressFieldsUpdate({ street: addr.street, city: addr.city, state: addr.state, zip5: addr.zip });
      });

      autocompleteRef.current = autocomplete;
    } catch (error) {
      console.error('Error initializing street Google Autocomplete:', error);
    }

    return () => {
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [editingItems.has("street")]);

  // Initialize Google Autocomplete for city field in Edit Project Requests
  useEffect(() => {
    const isEditing = editingItems.has("city");

    if (!isEditing || !cityInputRef?.current) {
      if (cityAutocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(cityAutocompleteRef.current);
        cityAutocompleteRef.current = null;
      }
      return;
    }

    if (!window.google?.maps?.places) {
      console.warn('Google Maps Places API not loaded');
      return;
    }

    try {
      const autocomplete = new window.google.maps.places.Autocomplete(cityInputRef.current, {
        componentRestrictions: { country: 'US' },
        fields: ["address_components"],
        types: ['(cities)'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place?.address_components) return;
        const addr = getGoogleAddressFields(place);
        handleAddressFieldsUpdate({ city: addr.city, state: addr.state });
      });

      cityAutocompleteRef.current = autocomplete;
    } catch (error) {
      console.error('Error initializing city Google Autocomplete:', error);
    }

    return () => {
      if (cityAutocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(cityAutocompleteRef.current);
        cityAutocompleteRef.current = null;
      }
    };
  }, [editingItems.has("city")]);

  // Initialize Google Autocomplete for zip5 field in Edit Project Requests
  useEffect(() => {
    const isEditing = editingItems.has("zip5");

    if (!isEditing || !zip5InputRef?.current) {
      if (zip5AutocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(zip5AutocompleteRef.current);
        zip5AutocompleteRef.current = null;
      }
      return;
    }

    if (!window.google?.maps?.places) {
      console.warn('Google Maps Places API not loaded');
      return;
    }

    try {
      const autocomplete = new window.google.maps.places.Autocomplete(zip5InputRef.current, {
        componentRestrictions: { country: 'US' },
        fields: ["address_components"],
        types: ['postal_code'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place?.address_components) return;
        const addr = getGoogleAddressFields(place);
        handleAddressFieldsUpdate({ zip5: addr.zip, city: addr.city, state: addr.state });
      });

      zip5AutocompleteRef.current = autocomplete;
    } catch (error) {
      console.error('Error initializing ZIP Google Autocomplete:', error);
    }

    return () => {
      if (zip5AutocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(zip5AutocompleteRef.current);
        zip5AutocompleteRef.current = null;
      }
    };
  }, [editingItems.has("zip5")]);

  // Initialize Google Autocomplete for street fields in phases
  useEffect(() => {
    if (!addedPhases?.length || !window.google?.maps?.places) {
      return;
    }

    addedPhases.forEach((phase, phaseIndex) => {
      const isEditing = phaseEditingItems.get(phaseIndex)?.has("street");
      const inputRef = phaseStreetRefs.current[phaseIndex];
      
      if (!isEditing || !inputRef) {
        // Cleanup autocomplete for this phase if editing stopped
        if (phaseAutocompleteRefs.current[phaseIndex]) {
          if (window.google?.maps?.event) {
            window.google.maps.event.clearInstanceListeners(phaseAutocompleteRefs.current[phaseIndex]);
          }
          delete phaseAutocompleteRefs.current[phaseIndex];
        }
        return;
      }

      // Skip if autocomplete already initialized for this phase
      if (phaseAutocompleteRefs.current[phaseIndex]) {
        return;
      }

      try {
        const autocompleteConfig = {
          componentRestrictions: { country: 'US' },
          fields: ["address_components", "geometry"],
          types: ['address']
        };

        const autocomplete = new window.google.maps.places.Autocomplete(inputRef, autocompleteConfig);

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (!place?.address_components) return;

          const address = getGoogleAddressFields(place);
          
          if (address.street) {
            handlePhaseValueModified(phaseIndex, "street", address.street, null);
          }
        });

        phaseAutocompleteRefs.current[phaseIndex] = autocomplete;
      } catch (error) {
        console.error('Error initializing Google Autocomplete for phase:', error);
      }
    });

    return () => {
      // Cleanup all phase autocompletes
      Object.values(phaseAutocompleteRefs.current).forEach(autocomplete => {
        if (autocomplete && window.google?.maps?.event) {
          window.google.maps.event.clearInstanceListeners(autocomplete);
        }
      });
      phaseAutocompleteRefs.current = {};
    };
  }, [addedPhases, phaseEditingItems]);

  useEffect(() => {
    if (show && request && projectInfo) {
      dispatch(initLenderRequest(request.requestTypeName));
      dispatch(fetchRequestStatuses());

      // Fetch restrictions for this project
      if (projectInfo.projectId) {
        dispatch(getCannedRestrictionsByProject(projectInfo.projectId));
      }

      if (request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS) {
        setAvailableFnmaStatuses([FM_STATUSES.NO_FANNIE_MAE_REVIEW, FM_STATUSES.APPROVED_BY_FANNIE_MAE]);
        setNewFNMAStatus(FM_STATUSES.NO_FANNIE_MAE_REVIEW);
      } else if (
        (request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS || request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS)
        && projectInfo.fnmaReviewStatus === FM_STATUSES.APPROVED_BY_FANNIE_MAE
      ) {
        setAvailableFnmaStatuses([FM_STATUSES.NO_FANNIE_MAE_REVIEW, FM_STATUSES.UNAVAILABLE]);
        setNewFNMAStatus(FM_STATUSES.NO_FANNIE_MAE_REVIEW);
      } else if (request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS || request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS) {
        setAvailableFnmaStatuses([FM_STATUSES.UNAVAILABLE]);
        setNewFNMAStatus(FM_STATUSES.UNAVAILABLE);
      // } else if (request.requestTypeName === "Risk Review Required") {
      //   setAvailableFnmaStatuses(["No Fannie Mae Review", "Fannie Mae Approval Expired"]);
      //   setNewFNMAStatus("No Fannie Mae Review");
      }

      // Set loading state at the start for ALL requests
      setIsLoadingStatusChangeData(true);

      (async () => {
        const allReasonCodes = await getReasonCodes();

        if (!allReasonCodes || !Array.isArray(allReasonCodes)) {
          console.error("Failed to fetch reason codes or received invalid data");
          setAllReasonCodes([]);
          setReasonCodeOptions([]);
          setIsLoadingStatusChangeData(false);
          return;
        }

        setAllReasonCodes(allReasonCodes);
        
        const reasonCodeOptions = allReasonCodes.map(rc => ({
          label: rc.reasonCode,
          value: rc.reasonId
        }));

        setReasonCodeOptions(reasonCodeOptions);

        // Fetch reason codes from the specific endpoint for Add/Remove Unavailable Status requests
        if (
          request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS
          || request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS
          || request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS
        ) {
          // For Modify Unavailable, also fetch active reason codes from FM Activity Feed
          if (request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS && projectInfo?.projectId) {
            try {
              const { data: activityData } = await axios.get(
                `${global.REACT_APP_API_ENDPOINT}/activityFeed/getFnmaReviewStatusActivityFeedForProject/${projectInfo.projectId}`
              );
              const fmActivity = activityData?.activityFeedFnmaStatusDTO || [];
              const latestEntry = fmActivity[0];
              
              if (latestEntry?.reasonIds && latestEntry?.fnmaReviewStatus === 'Unavailable') {
                const activeCodes = latestEntry.reasonIds
                  .map(id => allReasonCodes.find(code => code.reasonId === id))
                  .filter(Boolean)
                  .sort((a, b) => a.reasonCode.localeCompare(b.reasonCode));
                setActiveReasonCodes(activeCodes);
              }
            } catch (err) {
              console.error("Error fetching active reason codes from FM Activity Feed:", err);
            }
          }

          try {
            const { data } = await axios.get(
              `${global.REACT_APP_API_ENDPOINT}/lender/request/fetch-status-change-requests?requestId=${request.requestId}`
            );
            
            if (data && data.requestStatusChangeReasonCodes) {
              const fetchedReasonCodeIds = data.requestStatusChangeReasonCodes
                .filter(rc => rc.status !== "Modify")
                .map(rc => rc.reasonId);  

              // Store the reason code IDs in a Set for efficient lookups
              setReasonCodeIds(new Set(fetchedReasonCodeIds));

              // Filter allReasonCodes to get the full reason code objects matching the IDs
              const reasonCodeIdLookup = new Map(allReasonCodes.map(rc => [rc.reasonId, rc]));
              const selectedReasonCodes = fetchedReasonCodeIds.map(id => reasonCodeIdLookup.get(id)).filter(Boolean);

              setAddedReasonCodes(selectedReasonCodes);
            }
            
            // Store hasPersDecision from the response
            if (data && data.hasPersDecision !== undefined) {
              setHasPersDecision(data.hasPersDecision);
            }
          } catch (err) {
            console.error("Error fetching reason codes for status change request:", err);
          } finally {
            // Turn off loading AFTER all state has been set
            setIsLoadingStatusChangeData(false);
          }
        } else {
          // For non-status-change requests, turn off loading after reason codes are loaded
          setIsLoadingStatusChangeData(false);
        }
      })();
    }
  }, [show, request, projectInfo]);

  useEffect(() => {
    if (show && request && statusName && availableFnmaStatuses) {
      const showFnmaStatusDropdown = 
        canReview 
        && (
          statusName === REQUEST_STATUSES.UNDER_REVIEW 
          || statusName === REQUEST_STATUSES.ACTION_TAKEN 
        ) 
        && availableFnmaStatuses.length > 1;

      setShowFnmaStatusDropdown(showFnmaStatusDropdown);
    }
  }, [show, request, canReview, statusName, availableFnmaStatuses]);

  useEffect(() => {
    if (show && request && statusName && availableFnmaStatuses) {
      const shouldShowReasonCodes = canReview && statusName === REQUEST_STATUSES.ACTION_TAKEN &&
        (request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS || 
         request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS || 
         request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS);
      setShouldShowReasonCodes(shouldShowReasonCodes);
    }
  }, [show, request, canReview, statusName, availableFnmaStatuses]);

  useEffect(() => {
    if (projectInfo && restrictionsByProjectId[projectInfo.projectId]) {
      const cannedRestrictions = restrictionsByProjectId[projectInfo.projectId].preCannedDR || [];
      cannedRestrictions.sort(restrictionComparer);
      setRestrictions(cannedRestrictions);
    }
  }, [restrictionsByProjectId, projectInfo]);

  const getReasonCodes = async () => {
    try {
      const { data } = await axios.get(global.REACT_APP_API_ENDPOINT + `/reasonCodes/getAllReasonCodes`);

      if (data && Array.isArray(data)) {
        const reasonCodes = data.sort((a, b) => a.reasonCode.localeCompare(b.reasonCode));
        return reasonCodes;
      }
      
      console.error("Invalid reason codes data received:", data);
      return [];
    } catch (err) {
      console.error("Error fetching reason codes:", err);
      return [];
    }
  };

  // Update address fields in stagingChanges from a Google Autocomplete selection.
  // fieldUpdates is an object like { street, city, state, zip5 } — falsy values are skipped.
  // Only updates fields that already exist in stagingChanges (i.e. the lender requested them).
  function handleAddressFieldsUpdate(fieldUpdates) {
    const entries = Object.entries(fieldUpdates).filter(([, v]) => v);
    if (!entries.length) return;

    setStagingChanges(prev => {
      const next = [...prev];
      entries.forEach(([fieldName, newValue]) => {
        const index = next.findIndex(fc => fc.fieldName === fieldName);
        if (index !== -1) {
          next[index] = { ...next[index], newValue };
        }
      });
      return next;
    });

    setValidationErrors(prev => {
      const next = new Map(prev);
      entries.forEach(([fieldName]) => next.delete(fieldName));
      return next;
    });

    setLineItemApprovals(prev => {
      const next = new Map(prev);
      entries.forEach(([fieldName]) => next.set(fieldName, LINE_ITEM_STATUS.MODIFY));
      return next;
    });

    setEditingItems(prev => {
      const next = new Set(prev);
      entries.forEach(([fieldName]) => next.add(fieldName));
      return next;
    });
  }

  const handleFNMAStatusChange = (status) => {
    setNewFNMAStatus(status);
    
    // Clear expiration date when changing to any status except "No Fannie Mae Review" with hasPersDecision
    if (!(hasPersDecision && status === FM_STATUSES.NO_FANNIE_MAE_REVIEW)) {
      setApprovalExpiryDate(null);
    }
  };

  useEffect(() => {
    const canAddRestrictions = canReview && statusName === REQUEST_STATUSES.ACTION_TAKEN && (
      // request.requestTypeName === "Remove Approved By Fannie Mae Status" ||
      newFNMAStatus === FM_STATUSES.APPROVED_BY_FANNIE_MAE ||
      newFNMAStatus === FM_STATUSES.CONDITIONAL_APPROVAL
    ) && newFNMAStatus !== FM_STATUSES.UNAVAILABLE;

    // const canAddConditions = canReview && internalStatus === REQUEST_STATUSES.ACTION_TAKEN && 
    //   newFNMAStatus === "Conditional Approval";

    // Reason codes should only be editable when we're setting the new FM status to Unavailable.
    // If the status is anything else, show them read-only.
    const canAddReasonCodes = canReview &&
      (
        request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS
        || request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS
        || request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS
      ) &&
      newFNMAStatus === FM_STATUSES.UNAVAILABLE &&
      statusName === REQUEST_STATUSES.ACTION_TAKEN;

    setCanAddRestrictions(canAddRestrictions);
    // setCanAddConditions(canAddConditions);
    setCanAddReasonCodes(canAddReasonCodes);

    // Set expiration date to current date + 45 days when hasPersDecision and status is "No Fannie Mae Review"
    if (hasPersDecision && newFNMAStatus === FM_STATUSES.NO_FANNIE_MAE_REVIEW) {
      const expirationDate = moment().add(45, 'days').format('YYYY-MM-DD');
      setApprovalExpiryDate(expirationDate);
    } else {
      // Clear expiration date when changing to any other status (including "Approved By Fannie Mae" and "Unavailable")
      setApprovalExpiryDate(null);
    }

    if (statusName !== REQUEST_STATUSES.ACTION_TAKEN) {
      setAddedRestrictions([]);
      // Reset reason codes to original state using the Set
      const originalReasonCodes = allReasonCodes.filter(rc => reasonCodeIds.has(rc.reasonId));
      setAddedReasonCodes(originalReasonCodes);
      
      if (availableFnmaStatuses.includes(FM_STATUSES.NO_FANNIE_MAE_REVIEW)) {
        setNewFNMAStatus(FM_STATUSES.NO_FANNIE_MAE_REVIEW);
      } else {
        setNewFNMAStatus(availableFnmaStatuses[0] || "");
      }
    }
  }, [canReview, statusName, newFNMAStatus, hasPersDecision]);

  useEffect(() => {
    if (show && request && projectFieldsMap?.size && phaseFieldsMap?.size) {
      setRequestedDocuments([]);
      setLineItemApprovals(new Map());
      setEditingItems(new Set());
      setValidationErrors(new Map());
      setPhaseEditingItems(new Map());
      setPhaseValidationErrors(new Map());
      setPhaseLineItemApprovals(new Map());
      setNote("");  

      setStatusName(request.statusName);
      setInternalStatusName(request.internalStatus);
      baselineStatusRef.current = request.statusName;

      const passData = JSON.parse(sessionStorage.getItem("passData"));

      setCanReview(
        (isProjectStandardRole() || isAdminRole()) && (
          request.assignedToLdapId?.toLowerCase() === passData?.user.toLowerCase() ||
          getEnvName() === "local"
        ) && isReviewableStatus(request.statusName)
      );

      const documents = request.requestFeedDocumentDTOList;
      setDocuments(documents);      

      switch (request.requestTypeName) {
        case REQUEST_TYPES.LENDER_EDIT_PHASE:
        case REQUEST_TYPES.LENDER_EDIT_PROJECT:
        case REQUEST_TYPES.LENDER_ADD_PROJECT:
        case REQUEST_TYPES.LENDER_ADD_PHASE:
          (async () => {
            const {originalChanges, addedPhases} = await getStagingChanges(request.requestId);

            if (originalChanges) {
              setOriginalChanges(originalChanges);
              setStagingChanges(originalChanges.map(change => ({...change})));
            } else if (
              request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PHASE || 
              request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PROJECT
            ) {
              console.error("Failed to load staging changes for request", request, originalChanges);
            }

            if (addedPhases) {
              setOriginalAddedPhases(addedPhases);
              setAddedPhases(addedPhases.map(phase => ({...phase})));
            } else if (
              request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT || 
              request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PHASE
            ) {
              console.error("Failed to load phase list for request", request, addedPhases);
            }
          })();
          break;
      }
    }
  }, [show, request, projectFieldsMap, phaseFieldsMap]);

  useEffect(() => {
    if (show && request && request.statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED) {
      (async function() {
        const docRequestEvent = request.requestFeedActivityTrackerDTOList?.findLast(evt => 
          evt.externalStatus?.toLowerCase() === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED.toLowerCase() &&
          evt.activity?.toLowerCase() === "status changed"
        );
        
        if (docRequestEvent) {
          const requestActivityTrackerId = docRequestEvent.requestActivityTrackerId;
          const additionalDocuments = await getAdditionalDocuments(requestActivityTrackerId);
          setAdditionalDocuments(additionalDocuments);
        }
      })();
    }
  }, [show, request]);

  const isModified = fieldName => {
    const requestedChange = originalChanges.find(fc => fc.fieldName === fieldName);
    const modifiedChange = stagingChanges.find(fc => fc.fieldName === fieldName);
    return !!requestedChange && (requestedChange.newValue !== modifiedChange?.newValue);
  };

  // Derived flag: true when any requested document is "Other" (case-insensitive)
  const isOtherRequested = useMemo(
    () => requestedDocuments?.some(d => d.name?.toLowerCase() === "other"),
    [requestedDocuments]
  );

  // VALIDATION LOGIC FOR SUBMIT BUTTON
  // All logic for enabling/disabling the submit button is contained in this useEffect. 
  useEffect(() => {
    if (canReview && statusName && request) {
      // Shorthand variables to make the code more readable
      const actionTaken = statusName === REQUEST_STATUSES.ACTION_TAKEN;
      const noActionTaken = statusName === REQUEST_STATUSES.NO_ACTION_TAKEN;
      const partialActionTaken = statusName === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN;
      const infoRequired = statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED;
      const inProgressFromUnderReviewBaseline =
        statusName === REQUEST_STATUSES.IN_PROGRESS &&
        baselineStatusRef.current === REQUEST_STATUSES.UNDER_REVIEW;

      // Check if all line items are reviewed
      const allItemsReviewed = stagingChanges?.every(fieldChange => 
        lineItemApprovals?.has(fieldChange.fieldName)
        && (
          // If a line item is marked as "Modify", the value must be different
          lineItemApprovals?.get(fieldChange.fieldName) !== LINE_ITEM_STATUS.MODIFY 
          || isModified(fieldChange.fieldName)
        )
      );

      // Check if all phase items are reviewed, if any
      const allPhaseItemsReviewed = !addedPhases?.length || addedPhases.every(phase => phase.status);   
    
      // Check if there are any phase validation errors
      const hasPhaseValidationErrors = Array.from(phaseValidationErrors.values()).some(phaseErrors => 
        phaseErrors.size > 0
      );

      // Is this a Status Review request type?
      const isStatusReviewRequest = 
        request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS || 
        request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS || 
        request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS;
      
      // Check for required questions
      const isExpiryDateRequired = isStatusReviewRequest && actionTaken && newFNMAStatus === FM_STATUSES.APPROVED_BY_FANNIE_MAE;
      const isSuspensionReasonRequired = isStatusReviewRequest && actionTaken && hasPersDecision && newFNMAStatus === FM_STATUSES.NO_FANNIE_MAE_REVIEW;
      const areIneligibilityFieldsRequired = isStatusReviewRequest && actionTaken && hasPersDecision && newFNMAStatus === FM_STATUSES.UNAVAILABLE;

      // Check potentially required questions to see if they're empty
      const isExpiryDateEmpty = !approvalExpiryDate;
      const isSuspensionReasonEmpty = !suspensionReason.trim();
      const areIneligibilityFieldsEmpty = (
        !sellingServicingGuideRef.trim() || 
        !reviewedProjectExhibits.trim() || 
        !reasonForIneligibility.trim()
      );
      
      // If "Other" doc type is requested, notes are mandatory
      const isNoteMissing = isOtherRequested && !note.trim();

      // Reviewer can only submit the form if:
      // 1. There are no validation errors in the main form
      // 2. There are no validation errors in any phase, if applicable
      // 3. If the reason codes are editable, at least one must be selected
      // 4. If there are any required questions based on the new FM status, they can't be empty
      // 5. If "Other" doc type is requested, notes must be non-empty
      // 6. The request must have a review decision (ie accept, reject, partial accept, or additional info required)
      //    - If the status decision is Additional Information Required, then at least one document must be requested
      //    - If the status decision is Action Taken, then all phase items must be reviewed, if any
      //    - If the status decision is Partial Action Taken, then all line items and all phase items must be reviewed
      //    - If the status decision is No Action Taken, no line item review is required
      setCanSubmit(
        !validationErrors.size && 
        !hasPhaseValidationErrors && 
        (!canAddReasonCodes || addedReasonCodes?.length) &&
        (!isExpiryDateRequired || !isExpiryDateEmpty) &&
        (!isSuspensionReasonRequired || !isSuspensionReasonEmpty) &&
        (!areIneligibilityFieldsRequired || !areIneligibilityFieldsEmpty) &&
        !isNoteMissing &&
        (
          (statusName === REQUEST_STATUSES.IN_PROGRESS && !inProgressFromUnderReviewBaseline) ||
          (infoRequired && requestedDocuments?.length) ||
          (actionTaken && allPhaseItemsReviewed) || 
          (partialActionTaken && allItemsReviewed && allPhaseItemsReviewed) ||
          noActionTaken
        )
      );
    }
  }, [request, statusName, lineItemApprovals, phaseLineItemApprovals, stagingChanges, canReview, requestedDocuments, validationErrors, phaseValidationErrors, addedPhases, addedReasonCodes, newFNMAStatus, approvalExpiryDate, hasPersDecision, suspensionReason, sellingServicingGuideRef, reviewedProjectExhibits, reasonForIneligibility, canAddReasonCodes, note]);
       
  async function getStagingChanges(requestId) {
    const response = await axios.get(
      `${global.REACT_APP_API_ENDPOINT}/lender/request/projectStagingData?masterTrackerId=${requestId}`
    );
    
    if (response.status === 200) {
      let originalChanges = [];

      if (response.data?.stagingDetails?.length) {
        originalChanges = response.data.stagingDetails.map(change => {
          const fieldDef = request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PROJECT 
            ? projectFieldsMap.get(change.dataAttributeName)
            : phaseFieldsMap.get(change.dataAttributeName);
  
          return {
            fieldName: change.dataAttributeName,
            oldValue: parseBoolean(change.oldValue),
            newValue: parseBoolean(change.newValue),
            def: fieldDef,
            label: fieldDef?.formatLabel?.() || change.dataAttributeName,
            formatValue: fieldDef?.formatValue || ((value) => value || "(none)"),
          };
        });  
      }

      let addedPhases = null;

      if (response.data?.condoPhaseStagingDetails?.length) {
        addedPhases = response.data.condoPhaseStagingDetails.map(phaseDto => {
          return {
            phaseId: phaseDto.phaseId,
            phaseName: phaseDto.phaseName,
            street: phaseDto.street,
            city: phaseDto.city,
            state: phaseDto.state,
            zip5: phaseDto.zip5,
            buildingNumber: phaseDto.bldgNbr,
            unitNumber: phaseDto.unitNbr,
            unitCount: phaseDto.phaseUnitCount,
          };
        });
      }

      if (request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PHASE) {
        setPhaseName(response.data?.phaseName);
        setPhaseId(response.data?.phaseId);
      }

      return {originalChanges, addedPhases};
    }

    console.error("Error fetching requested edits:", response);
    return {};
  }

  const handleClose = () => {
    onClose();
  }

  const handleAddReasonCodes = (values) => {
    const selectedReasonCodes = allReasonCodes.filter(rc => 
      values.find(v => v.value === rc.reasonId)
    );
    setAddedReasonCodes(selectedReasonCodes);
  };

  const handleRemoveDocType = (docTypeKey) => {
    setRequestedDocuments(prevDocTypes => prevDocTypes.filter(dt => dt.documentRankingId !== docTypeKey));
  }

  const handleAddDocType = (docTypeKey) => {
    // Use availableDocuments if we're in "Additional Information Required" mode, otherwise use documents
    const sourceDocuments = statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED 
      ? availableDocuments 
      : documents;
    
    const docType = sourceDocuments.find(dt => dt.documentRankingId.toString() === docTypeKey);

    if (docType && !requestedDocuments.find(dt => dt.documentRankingId === docType.documentRankingId)) {
      const normalizedDocType = {
        ...docType,
        documentName: docType.documentName ?? docType.name
      };
      setRequestedDocuments(prevDocTypes => [...prevDocTypes, normalizedDocType]);
    }
  }

  const handleRemoveRestriction = (restrictionKey) => {
    setAddedRestrictions(prevRestrictions => prevRestrictions.filter(r => {
      const key = (r.restrictionId != null) ? String(r.restrictionId) : (r.restrictionName ?? r.restrictionsName);
      return key !== restrictionKey;
    }));
  }

  const handleAddRestriction = (restrictionKey) => {
    const restriction = restrictions.find(r => {
      const key = (r.restrictionId != null) ? String(r.restrictionId) : (r.restrictionName ?? r.restrictionsName);
      return key === restrictionKey;
    });

    if (!restriction) return;

    const alreadyAdded = addedRestrictions.some(r => {
      if (r.restrictionId != null && restriction.restrictionId != null) {
        return r.restrictionId === restriction.restrictionId;
      }
      return (r.restrictionName ?? r.restrictionsName) === (restriction.restrictionName ?? restriction.restrictionsName);
    });

    if (!alreadyAdded) {
      setAddedRestrictions(prevRestrictions => [...prevRestrictions, restriction]);
    }
  }

  // const handleRemoveCondition = (conditionKey) => {
  //   setAddedConditions(prevConditions => prevConditions.filter(c => c.conditionName !== conditionKey));
  // }

  // const handleAddCondition = (conditionKey) => {
  //   const condition = allConditions.find(c => c.conditionName === conditionKey);

  //   if (condition && !addedConditions.find(c => c.conditionName === condition.conditionName)) {
  //     setAddedConditions(prevConditions => [...prevConditions, condition]);
  //   }
  // }

  const handleLineItemStatus = (fieldName, lineItemStatus) => {
    setLineItemApprovals(prev => new Map(prev).set(fieldName, lineItemStatus));
    
    // If projectStyle was requested to be changed to "Other" and that change is rejected, styleComments change must be rejected
    if (fieldName === "projectStyle") {
      const projectStyleChange = stagingChanges.find(fc => fc.fieldName === "projectStyle");
      if (projectStyleChange) {
        if (projectStyleChange.newValue === "Other") {
          const styleCommentsChange = stagingChanges.find(fc => fc.fieldName === "styleComments");

          // styleComments should be added/removed automatically when projectStyle is set to/from Other
          if (!styleCommentsChange) {
            console.error("styleComments change not found when projectStyle is Other", {stagingChanges, projectStyleChange});
            throw new Error("styleComments change not found when projectStyle is Other");
          }

          if (lineItemStatus === LINE_ITEM_STATUS.REJECT) {
            setLineItemApprovals(prev => new Map(prev).set("styleComments", LINE_ITEM_STATUS.REJECT));
            setEditingItems(prev => { const next = new Set(prev); next.delete("styleComments"); return next; });
          } else {
            setLineItemApprovals(prev => new Map(prev).set("styleComments", LINE_ITEM_STATUS.ACCEPT));
          }
        } else {
          // If projectStyle is changed to a value other than "Other", remove any styleComments change
          setLineItemApprovals(prev => {
            const next = new Map(prev);
            next.delete("styleComments");
            return next;
          });

          const styleCommentsIndex = stagingChanges.findIndex(fc => fc.fieldName === "styleComments");
          if (styleCommentsIndex !== -1) {
            setStagingChanges(prev => { 
              const next = [...prev]; 
              next.splice(styleCommentsIndex, 1); 
              return next; 
            });
          }
        }
      }
    }
    
    if (lineItemStatus === LINE_ITEM_STATUS.MODIFY && !editingItems.has(fieldName)) {
      setEditingItems(prev => new Set(prev).add(fieldName));
    } else if (lineItemStatus !== LINE_ITEM_STATUS.MODIFY && editingItems.has(fieldName)) {
      setEditingItems(prev => { 
        const next = new Set(prev); 
        next.delete(fieldName); 
        return next; 
      });
      undoModifyField(fieldName);
    }
  };

  const handleFieldEditing = (e, fieldName) => {
    e?.stopPropagation();
    setEditingItems(prev => new Set(prev).add(fieldName));
  };

  const cancelFieldEditing = (e, fieldName) => {
    e?.stopPropagation();
    // // Use a short delay to give other events a chance to run
    // DelayFor(30).then(() => {
      setEditingItems(prev => { 
        const _editingItems = new Set(prev); 
        _editingItems.delete(fieldName); 
        return _editingItems; 
      });
      undoModifyField(fieldName);
    // });
  };

  const undoModifyField = (fieldName) => {
    const modified = stagingChanges.find(fc => fc.fieldName === fieldName);
    const original = originalChanges.find(fc => fc.fieldName === fieldName);

    if (modified && original) {
      // revert the field value back to the original value in stagingChanges
      setStagingChanges(prev => {
        const next = [...prev];
        const index = next.findIndex(fc => fc.fieldName === fieldName);
        if (index !== -1) {
          next[index] = { ...original };
        }
        return next;
      });

      setValidationErrors(prev => {
        const next = new Map(prev);
        next.delete(fieldName);
        return next;
      });

      // If (and only if) the field being reverted is "projectStyle", we also need to revert "styleComments"
      if (fieldName === "projectStyle") {    
        setStagingChanges(prev => {
          const next = [...prev];
          const styleCommentsChangeIndex = next.findIndex(fc => fc.fieldName === "styleComments");
          if (styleCommentsChangeIndex !== -1) {
            next.splice(styleCommentsChangeIndex, 1);
          }
          return next;
        });

        if (original.newValue === "Other") {          
          setStagingChanges(prev => {
            const next = [...prev];
            const projectStyleChangeIndex = next.findIndex(fc => fc.fieldName === "projectStyle");
            const styleCommentsChange = originalChanges.find(fc => fc.fieldName === "styleComments");
            next.splice(projectStyleChangeIndex + 1, 0, { ...styleCommentsChange });
            return next;
          });
        }

        setValidationErrors(prev => {
          const next = new Map(prev);
          next.delete("styleComments");
          return next;
        });
      }
    }
  };

  const handleValueModified = (fieldName, modifiedValue, validationMessage, changeIndex) => {
    setStagingChanges(prev => {
      const next = [...prev];
      const index = next.findIndex(fc => fc.fieldName === fieldName);
      if (index !== -1) {
        next[index] = {
          ...next[index], 
          newValue: modifiedValue
        };
      }
      return next;
    });

    setValidationErrors(prev => {
      const next = new Map(prev);
      if (validationMessage) {
        next.set(fieldName, validationMessage);
      } else {
        next.delete(fieldName);
      }
      return next;
    });

    setLineItemApprovals(prev => new Map(prev).set(fieldName, LINE_ITEM_STATUS.MODIFY));

    // If (and only if) the field being modified is "projectStyle", we also need to add or remove "styleComments"
    // depending on whether the new value is "Other".
    // - If the new value is "Other":
    //   - If it's different from the original value, add a new change for "styleComments" right after "projectStyle" change.
    //   - If it's the same as the original value, revert any existing "styleComments" change back to original value.
    // - If the new value is anything other than "Other", remove any "styleComments" change if it exists.
    if (fieldName === "projectStyle") {
      if (modifiedValue === "Other") {
        // Try to find a pre-existing styleComments change from originalChanges
        let styleCommentsChange = originalChanges.find(fc => fc.fieldName === "styleComments");

        // If no pre-existing styleComments change exists, create a new one with empty values
        if (!styleCommentsChange) {
          const styleCommentsDef = projectFieldsMap.get("styleComments");
          styleCommentsChange = {
            fieldName: "styleComments",
            newValue: "",
            oldValue: "",
            def: styleCommentsDef,
            label: styleCommentsDef?.formatLabel?.() || "Style Comments",
            formatValue: styleCommentsDef?.formatValue || ((value) => value || "(none)"),
          };
        }

        setStagingChanges(prev => {
          const next = [...prev];
          const projectStyleChangeIndex = next.findIndex(fc => fc.fieldName === "projectStyle");
          next.splice(projectStyleChangeIndex + 1, 0, styleCommentsChange);
          return next;
        });

        // put new styleComments change into editing mode and set its approval to Modify
        setEditingItems(prev => new Set(prev).add("styleComments"));

        setValidationErrors(prev => {
          const next = new Map(prev);
          next.delete("styleComments");
          return next;
        });

        // if (statusName === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN) {
          setLineItemApprovals(prev => new Map(prev).set("styleComments", LINE_ITEM_STATUS.MODIFY));
        // }
      } else {
        // Remove any existing styleComments change
        setStagingChanges(prev => prev.filter(fc => fc.fieldName !== "styleComments"));

        setLineItemApprovals(prev => {
          const next = new Map(prev);
          next.delete("styleComments");
          return next;
        });
        
        setValidationErrors(prev => {
          const next = new Map(prev);
          next.delete("styleComments");
          return next;
        });
      }
    }
  }

  const handlePhaseValueModified = (phaseIndex, fieldName, modifiedValue, validationMessage) => {
    const phase = addedPhases[phaseIndex];
    const originalPhase = originalAddedPhases[phaseIndex];

    if (phase) {
      setAddedPhases(prev => {
        const next = [...prev];
        const updatedPhase = { ...next[phaseIndex], [fieldName]: modifiedValue };
        updatedPhase.status = updatedPhase[fieldName] !== originalPhase[fieldName] ? LINE_ITEM_STATUS.MODIFY : LINE_ITEM_STATUS.ACCEPT;
        next[phaseIndex] = updatedPhase;
        return next;
      });

      setPhaseValidationErrors(prev => {
        const next = new Map(prev);

        if (!next.has(phaseIndex)) {
          next.set(phaseIndex, new Map());
        }

        const phaseErrors = new Map(next.get(phaseIndex));

        if (validationMessage) {
          phaseErrors.set(fieldName, validationMessage);
        } else {
          phaseErrors.delete(fieldName);
        }

        next.set(phaseIndex, phaseErrors);
        return next;
      });

      setPhaseLineItemApprovals(prev => {
        const next = new Map(prev);
        const currentPhaseStatus = next.get(phaseIndex) || LINE_ITEM_STATUS.ACCEPT;

        if (currentPhaseStatus !== LINE_ITEM_STATUS.MODIFY) {
          const originalValue = originalPhase ? originalPhase[fieldName] : null;
          const newValue = modifiedValue;
          const newStatus = (newValue !== originalValue) ? LINE_ITEM_STATUS.MODIFY : LINE_ITEM_STATUS.ACCEPT;
          next.set(phaseIndex, newStatus);
        }

        return next;
      });
    }
  };

  const handlePhaseValidation = (phaseIndex, fieldName, validationMessage) => {
    setPhaseValidationErrors(prev => {
      const next = new Map(prev);

      if (!next.has(phaseIndex)) {
        next.set(phaseIndex, new Map());
      }

      const phaseErrors = new Map(next.get(phaseIndex));

      if (validationMessage) {
        phaseErrors.set(fieldName, validationMessage);
      } else {
        phaseErrors.delete(fieldName);
      }

      next.set(phaseIndex, phaseErrors);
      return next;
    });
  };

  const handleStatusChange = (newStatus) => {
    if (newStatus !== statusName) {
      setStatusName(newStatus);
      setRequestedDocuments([]);
      setLineItemApprovals(new Map());
      setEditingItems(new Set());
      setValidationErrors(new Map());
      setPhaseEditingItems(new Map());
      setPhaseValidationErrors(new Map());
      setPhaseLineItemApprovals(new Map());
      setStagingChanges(originalChanges?.map(change => ({...change})));

      // Populate available documents from Redux state when Additional Information Required is selected
      if (newStatus === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED) {
        if (allDocTypesById?.size) {
          const sortedDocs = Array.from(allDocTypesById.values())
            .filter(doc => doc.active)
            .map(doc => ({
              ...doc,
              documentName: doc.documentName ?? doc.name
            }))
            .sort((a, b) => {
              const aIsOther = a.name?.toLowerCase() === "other";
              const bIsOther = b.name?.toLowerCase() === "other";
              if (aIsOther && !bIsOther) return 1;
              if (!aIsOther && bIsOther) return -1;
              if (a.required && !b.required) return -1;
              if (!a.required && b.required) return 1;
              return (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" });
            });

          setAvailableDocuments(sortedDocs);
        } else {
          setAvailableDocuments([]);
        }
      } else {
        setAvailableDocuments([]);
      }

      if (originalAddedPhases) {        
        const addedPhases = originalAddedPhases.map(phase => ({
          ...phase,
          status: (newStatus === REQUEST_STATUSES.ACTION_TAKEN || newStatus === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN)
            ? LINE_ITEM_STATUS.ACCEPT
            : newStatus === REQUEST_STATUSES.NO_ACTION_TAKEN
            ? LINE_ITEM_STATUS.REJECT
            : undefined
        }));

        setAddedPhases([...addedPhases]);
      }

      if (availableFnmaStatuses.includes(FM_STATUSES.NO_FANNIE_MAE_REVIEW)) {
        setNewFNMAStatus(FM_STATUSES.NO_FANNIE_MAE_REVIEW);
      }
    }
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    
    // Use a short delay to give the other handlers a chance to run
    DelayFor(40).then(async () => {
      // In the backend, "Under Review" is the stored status that maps to "In Progress" in the UI
      const validateStatusName = statusName === REQUEST_STATUSES.IN_PROGRESS
        ? REQUEST_STATUSES.UNDER_REVIEW
        : statusName;
      const statusId = requestStatuses.find(
        status => status.statusName === validateStatusName)?.projectRequestStatusId;

      const passData = JSON.parse(sessionStorage.getItem("passData"));

      // Check if this is a status change request (Add/Remove Unavailable Status)
      if (
        request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS
        || request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS
        || request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS
      ) {
        // Build reasonCodesDetails with proper status
        const currentReasonCodeIds = new Set(addedReasonCodes.map(rc => rc.reasonId));
        
        // Get all unique reason code IDs from both reference and current
        const allReasonCodeIds = new Set([...reasonCodeIds, ...currentReasonCodeIds]);

        const reasonCodesDetails = Array.from(allReasonCodeIds).map(reasonId => {
          const inReference = reasonCodeIds.has(reasonId);
          const inCurrent = currentReasonCodeIds.has(reasonId);
          
          let status;
          if (inCurrent && !inReference) {
            // Not present in reference but present in current = Modify (newly added)
            status = LINE_ITEM_STATUS.MODIFY;
          } else if (inCurrent && inReference) {
            // Present in both = Accept (unchanged)
            status = LINE_ITEM_STATUS.ACCEPT;
          } else if (!inCurrent && inReference) {
            // Not present in current but present in reference = Reject (removed)
            status = LINE_ITEM_STATUS.REJECT;
          }
          
          return {
            reasonCodeId: reasonId,
            status: status
          };
        });

        const statusChangePayload = {
          statusId,
          createdBy: passData.user,
          assignTo: request.assignedToLdapId || passData.user,
          fmReviewStatus: newFNMAStatus,
          expirationDate: approvalExpiryDate || null,
          ...(reasonCodesDetails ? { reasonCodesDetails } : {}),
          notes: note,
          notesIsExternal: notesExternal,
          documentRankingList: requestedDocuments.map(dt => dt.documentRankingId),
          deliveryRestrictions: addedRestrictions
            .map(r => r.restrictionName ?? r.restrictionsName)
            .filter(Boolean),
          suspensionReason: hasPersDecision && newFNMAStatus === FM_STATUSES.NO_FANNIE_MAE_REVIEW ? suspensionReason : null,
          persDecisionIneligible: hasPersDecision && newFNMAStatus === FM_STATUSES.UNAVAILABLE ? {
            sellingGuideReference: sellingServicingGuideRef,
            reviewedProjectExhibits: reviewedProjectExhibits,
            ineligibilityExplaination: reasonForIneligibility
          } : null
        };

        const response = await axios.post(
          `${global.REACT_APP_API_ENDPOINT}/lender/request/review-status-request/${request.requestId}/${projectInfo.projectId}`,
          statusChangePayload
        );

        if (response.status === 200) {
          onClose();
          window.location.reload();
        } else {
          console.error("Error submitting status change review:", response);
        }
      } else {
        // Original logic for other request types
        const isEditRequestType =
          request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PROJECT ||
          request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PHASE;
        const shouldIncludeStagingData = !(isEditRequestType && statusName === REQUEST_STATUSES.IN_PROGRESS);

        const payload = {
          requestMasterTrackerId: request.requestId,
          statusId,
          note,
          notesIsExternal: notesExternal,
          requestType: request.requestTypeName,
          assignTo: request.assignedToLdapId || passData.user,
          createdBy: passData.user,
          reviewByLdapId: passData.user,
          documentRankingList: requestedDocuments.map(dt => dt.documentRankingId),
          ...(shouldIncludeStagingData
            ? {
                stagingList: stagingChanges.map(change => {
                  const originalChange = originalChanges.find(oc => oc.fieldName === change.fieldName);

                  return {
                    // projectStagingId: ?,
                    dataAttributeName: change.fieldName,
                    newValue: change.newValue,
                    oldValue: change.oldValue,
                    status: statusName === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN
                      ? lineItemApprovals.get(change.fieldName)
                      : statusName === REQUEST_STATUSES.NO_ACTION_TAKEN
                      ? LINE_ITEM_STATUS.REJECT
                      : change.newValue === originalChange?.newValue
                      ? LINE_ITEM_STATUS.ACCEPT
                      : LINE_ITEM_STATUS.MODIFY,
                    requestMasterTrackerId: request.requestId,
                  };
                }),

                // todo: send entire phase once backend supports it
                // phaseStatusList: addedPhases,

                // temp for now:
                phaseStatusList: addedPhases?.map(phase => {
                  return {
                    phaseId: phase.phaseId,
                    status: phase.status
                  };
                })
              }
            : {})
        };

        const response = await axios.post(
          `${global.REACT_APP_API_ENDPOINT}/lender/request/saveReviewDetails`,
          payload
        );

        if (response.status === 200) {
          onClose();
          window.location.reload();
        } else {
          console.error("Error submitting review:", response);
        }
      }
    }).finally(() => setIsSubmitting(false));
  }

  function phasesAreEqual(phaseA, phaseB) {
    return phaseA.phaseId === phaseB.phaseId &&
      phaseA.phaseName === phaseB.phaseName &&
      phaseA.street === phaseB.street &&
      phaseA.buildingNumber === phaseB.buildingNumber &&
      phaseA.unitNumber === phaseB.unitNumber &&
      phaseA.unitCount === phaseB.unitCount // &&
      // phaseA.city === phaseB.city &&
      // phaseA.state === phaseB.state &&
      // phaseA.zip5 === phaseB.zip5 &&
  };

  // Special case for status notes when the existing status is Additional Information Received: Instead
  // of the text of the note, show the internal/external status label.
  function getStatusNoteDisplay(note) {
    return note === "Additional documents received." && !isInternal()
          ? REQUEST_NOTES.ADDITIONAL_DOCUMENTS_SUBMITTED
          : note;
  }

  return (request && (
    <Modal
      show={show}
      backdrop="static"
      className={`request-details-modal ${
        statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED ? "request-more-info" :
        statusName === REQUEST_STATUSES.ACTION_TAKEN ? "request-approved" : 
        statusName === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN ? "request-partial" :
        statusName === REQUEST_STATUSES.NO_ACTION_TAKEN ? "request-rejected" : 
        statusName === REQUEST_STATUSES.UNRESPONSIVE ? "request-unresponsive" :
        ""
      }`}
      onHide={handleClose}
      size="lg"
      centered>
      <Modal.Header className="modal-header" closeButton>
        <Modal.Title className="modal-title">Request Details</Modal.Title>
      </Modal.Header>
      {show ? (
        <Modal.Body className="modal-body" style={{ position: 'relative' }}>
          {isLoadingStatusChangeData && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(3px)',
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                  <span className="sr-only">Loading...</span>
                </div>
                <div style={{ marginTop: '15px', color: '#333', fontWeight: 'bold' }}>Loading request details...</div>
              </div>
            </div>
          )}
          <div className="request-details-section" style={{flexDirection: "row"}}>
            <div className="request-details-value">
              <b>Type:</b> {request.requestTypeName}
            </div>
            <div className="request-details-value">
              <b>Date:</b> {moment(request.requestCreatedDate).format("MM/DD/YY")}
            </div>
            {phaseName ? (
              <div className="request-details-value">
                <b>Phase Name: </b> {phaseName}
              </div>
            ) : <></>}
            {phaseId ? (
              <div className="request-details-value">
                <b>Phase ID: </b> {phaseId}
              </div>
            ) : <></>}
            {isProjectRole() || isAdminRole() ? (
              <>
                <div className="request-details-value">
                  <b>Requested By:</b> {request.requestCreatedByName || request.requestCreatedByLdapId || "(n/a)"}
                </div>
                <div className="request-details-value">
                  <b>Lender:</b> {request.lenderName || "(n/a)"}
                </div>
                <div className="request-details-value">
                  <b>Assigned To:</b> {request.assignedToName || request.assignedToLdapId || "N/A"}
                </div>
                <div className="request-details-value">
                  <b>Status: </b> 
                  {canReview ? (
                    <Dropdown className="link-dropdown" onSelect={handleStatusChange}>
                      <Dropdown.Toggle variant="link">
                        {getInternalDisplayStatus(statusName, internalStatusName)}
                      </Dropdown.Toggle>

                      
                      {/* TODO: The hardcoded labels below are a temporary solution for displaying these statuses
                      before the upcoming release, using the simplest code possible to minimize risk. After the 
                      release, both internal and external labels for all statuses will be refactored and 
                      centralized. */}
                      <Dropdown.Menu>
                        {request.statusName !== REQUEST_STATUSES.IN_PROGRESS && request.statusName !== REQUEST_STATUSES.UNDER_REVIEW && (
                          <Dropdown.Item eventKey={REQUEST_STATUSES.IN_PROGRESS}>
                            In Progress
                          </Dropdown.Item>
                        )}
                        {request.statusName !== REQUEST_STATUSES.UNRESPONSIVE && (
                          <Dropdown.Item eventKey={REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED}>
                            Additional Info Required
                          </Dropdown.Item>
                        )}
                        <DropdownSubmenu title="Reviewed">
                          <Dropdown.Item eventKey={REQUEST_STATUSES.ACTION_TAKEN}>
                            Completed
                          </Dropdown.Item>
                          {(
                            request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PROJECT || 
                            request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PHASE
                          ) && (
                            <Dropdown.Item eventKey={REQUEST_STATUSES.PARTIAL_ACTION_TAKEN}>
                              Partially Completed
                            </Dropdown.Item>
                          )}
                          <Dropdown.Item eventKey={REQUEST_STATUSES.NO_ACTION_TAKEN}>
                            Declined
                          </Dropdown.Item>
                        </DropdownSubmenu>
                      </Dropdown.Menu>
                    </Dropdown>
                  ) : (
                    <span>{getInternalDisplayStatus(statusName, internalStatusName)}</span>
                  )}
                </div>
              </>
            ) : (
              <div className="request-details-value">
                <b>Status: </b> {request.externalStatus}
              </div>
            )}
            <div className="request-details-value" style={{flexBasis: '100%'}}>
              <b style={{whiteSpace: 'nowrap'}}>Status Note:</b> {getStatusNoteDisplay(statusNoteText)}
            </div>
          </div>

          {request.statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED ? (
            <div className="request-details-section documents-required-section">
              <div className="request-details-column">
                {isLenderRole() || isTPORole() ? (
                  <>
                    <h3 className="request-details-subheader warning">
                      <WarningOutlined className="warning-icon" />
                      Documentation Required
                    </h3>
                    <p style={{marginBottom: 0}}>
                      <b>Fannie Mae requires the following additional documents to proceed with this request:</b>
                    </p>
                  </>
                ) : (
                  <h3 className="request-details-subheader">
                    Additional Documentation Required
                  </h3>
                )}
                {additionalDocuments.map(doc => (
                  <div key={doc.documentRankingId} className="request-doc-item">
                    <div className="request-doc-value">
                      <GrDocumentText style={{marginRight: 5}} /> 
                      {doc.documentName}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (<></>)}

          {request.statusName === REQUEST_STATUSES.UNRESPONSIVE && (isLenderRole() || isTPORole()) ? (
            <div className="request-details-section unresponsive-guidance-section">
              <div className="request-details-column">
                <h3 className="request-details-subheader warning">
                  <WarningOutlined className="warning-icon" />
                  Request Closed
                </h3>
                <p style={{ marginBottom: 0 }}>
                  <b>This request has been closed due to no response. Please submit a new request when you are ready to proceed.</b>
                </p>
              </div>
            </div>
          ) : (<></>)}

          {/* todo: replace custom styles and plain divs with responsive classes */}
          <div className="request-details-section" style={{gap: 15}}>
            {(
              request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS 
              || request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS 
              || request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS 
            ) ? (
              <>
                {statusName === REQUEST_STATUSES.ACTION_TAKEN && canReview ? (
                  <div className="request-details-column">
                    <h3 className="request-details-subheader">
                      {request.requestTypeName}
                    </h3>
                    <div>
                      <div>
                        {/* <div>
                          <b>Current FM Review Status: </b> {projectInfo?.fnmaReviewStatus || "N/A"}
                        </div> */}
                        <div className="request-change-value new-value">
                          <b>New FM Review Status:</b>
                          {showFnmaStatusDropdown ? (
                            <Dropdown 
                              className={"link-dropdown field-value "} 
                              onSelect={status => handleFNMAStatusChange(status)} 
                              value={newFNMAStatus}
                            >
                              <Dropdown.Toggle variant="link">
                                {newFNMAStatus || "Select Status"}
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                {availableFnmaStatuses.map((status) => (
                                  <Dropdown.Item key={status} eventKey={status}>
                                    {status}
                                  </Dropdown.Item>
                                ))}
                              </Dropdown.Menu>
                            </Dropdown>
                          ) : (
                            <span className={"editable-read-only field-value"}>
                              {newFNMAStatus}
                            </span>
                          )}
                        </div>
                      </div>
                      {newFNMAStatus === FM_STATUSES.APPROVED_BY_FANNIE_MAE && (
                        <div className="form-group" style={{ marginTop: '15px' }}>
                          <label style={{ fontWeight: 'bold' }}>Expiry Date <span style={{ color: 'red' }}>*</span></label>
                          <DatePicker
                            id="approvalExpiryDate"
                            label=""
                            placeholder={"Expiry Date"}
                            name={approvalExpiryDate}
                            changeHandler={(e) => {
                              if (new Date(e.target.value) >= new Date("1776-01-01")) {
                                setApprovalExpiryDate(e.target.value);
                              } else {
                                setApprovalExpiryDate(null);
                              }
                            }}
                            value={approvalExpiryDate}
                            datePickerWidth="150px"
                            min="1776-01-01"
                          />
                        </div>
                      )}
                      {hasPersDecision && newFNMAStatus === FM_STATUSES.NO_FANNIE_MAE_REVIEW && (
                        <>
                          <div className="form-group" style={{ marginTop: '15px' }}>
                            <label style={{ fontWeight: 'bold' }}>Expiration Date</label>
                            <DatePicker
                              id="suspensionExpiryDate"
                              label=""
                              placeholder={"Expiration Date"}
                              name={approvalExpiryDate}
                              changeHandler={() => {}}
                              value={approvalExpiryDate || moment().add(45, 'days').format('YYYY-MM-DD')}
                              datePickerWidth="150px"
                              min="1776-01-01"
                              disabled={true}
                            />
                          </div>
                          <div className="form-group" style={{ marginTop: '15px' }}>
                            <label style={{ fontWeight: 'bold' }}>Reason for Suspension <span style={{ color: 'red' }}>*</span></label>
                            <textarea
                              className="form-control"
                              rows="4"
                              maxLength={2000}
                              value={suspensionReason}
                              onChange={(e) => setSuspensionReason(e.target.value)}
                              placeholder="Enter reason for suspension (required)"
                              style={{ width: '100%', resize: 'vertical' }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                              {suspensionReason.length}/2000 characters
                            </div>
                          </div>
                        </>
                      )}
                      {hasPersDecision && newFNMAStatus === FM_STATUSES.UNAVAILABLE && (
                        <>
                          <div className="form-group" style={{ marginTop: '15px' }}>
                            <label style={{ fontWeight: 'bold' }}>Selling and Servicing Guide Reference *</label>
                            <input
                              type="text"
                              className="form-control"
                              maxLength={200}
                              value={sellingServicingGuideRef}
                              onChange={(e) => setSellingServicingGuideRef(e.target.value)}
                              placeholder="Enter Selling and Servicing Guide Reference (required)"
                              style={{ width: '100%' }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                              {sellingServicingGuideRef.length}/200 characters
                            </div>
                          </div>
                          <div className="form-group" style={{ marginTop: '15px' }}>
                            <label style={{ fontWeight: 'bold' }}>List of reviewed Project Exhibits *</label>
                            <input
                              type="text"
                              className="form-control"
                              maxLength={200}
                              value={reviewedProjectExhibits}
                              onChange={(e) => setReviewedProjectExhibits(e.target.value)}
                              placeholder="Enter List of reviewed Project Exhibits (required)"
                              style={{ width: '100%' }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                              {reviewedProjectExhibits.length}/200 characters
                            </div>
                          </div>
                          <div className="form-group" style={{ marginTop: '15px' }}>
                            <label style={{ fontWeight: 'bold' }}>Reason for Ineligibility *</label>
                            <textarea
                              className="form-control"
                              rows="4"
                              maxLength={2000}
                              value={reasonForIneligibility}
                              onChange={(e) => setReasonForIneligibility(e.target.value)}
                              placeholder="Enter reason for ineligibility (required)"
                              style={{ width: '100%', resize: 'vertical' }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                              {reasonForIneligibility.length}/2000 characters
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (<></>)}

                {canAddRestrictions ? (
                  <div className="request-details-column">                    
                    <h3 className="request-details-subheader">
                      Delivery Restrictions
                      {/* <RestrictionsTooltip restrictions={restrictions}/> */}
                    </h3>
                    <div style={{maxHeight: '100px', overflow: 'auto' }}>
                      {restrictions.map((restriction, index) => {
                        const restrictionKey = restriction.restrictionId != null
                          ? String(restriction.restrictionId)
                          : (restriction.restrictionName ?? restriction.restrictionsName);

                        const existingIndex = addedRestrictions.findIndex(r => {
                          if (r.restrictionId != null && restriction.restrictionId != null) {
                            return r.restrictionId === restriction.restrictionId;
                          }
                          return (r.restrictionName ?? r.restrictionsName) === (restriction.restrictionName ?? restriction.restrictionsName);
                        });

                        return (
                          <div key={index} style={{display: 'flex', flexDirection: 'row', gap: '10px', marginBottom: '5px' }}>                      
                            <input
                              type="checkbox"
                              className={"checkboxfield"}
                              style={{ height: 15, width: 15, marginTop: 5 }}
                              id={`restriction_${index + 1}`}
                              name={`restriction_${index + 1}`}
                              checked={existingIndex !== -1}
                              aria-label={`restriction_${index + 1}`}
                              onChange={(event) => {
                                const isSelected = event.target.checked;

                                if (isSelected && existingIndex === -1) {
                                  handleAddRestriction(restrictionKey);
                                } else if (!isSelected && existingIndex !== -1) {
                                  handleRemoveRestriction(restrictionKey);
                                }
                              }}
                            />
                            <div>
                              <div>{restriction.restrictionName ?? restriction.restrictionsName}</div>
                              <div>{restriction.restrictionDescription ?? restriction.restrictionsDescription}</div>
                            </div>
                          </div>
                        )}) ?? <></>}
                    </div>
                  </div>
                ) : (<></>)}
                      
                {/* {canAddConditions ? (
                  <>
                    <div style={{marginTop: '20px' }}>
                      <div style={{color: '#0e51d8', fontSize: 16, fontWeight: '600', paddingBottom: '5px'}}>
                      <div style={{color: '#085280', fontSize: 16, fontWeight: '600', paddingBottom: '5px' }}></div>
                        <img src={WarningIcon} alt="Warning Logo"/>
                        &nbsp; Approval Conditions
                      </div>
                      <div style={{maxHeight: '100px', overflow: 'auto' }}>
                        {allConditions?.map((condition, index) => {
                          const conditionKey = condition.conditionName;
                          const existingIndex = addedConditions.findIndex(r => r.conditionName === conditionKey);

                          return (
                            <div key={index} style={{display: 'flex', flexDirection: 'row', gap: '10px', marginBottom: '5px' }}>                      
                              <input
                                type="checkbox"
                                className={"checkboxfield"}
                                style={{ height: 15, width: 15, marginTop: 5 }}
                                id={`condition_${index}`}
                                name={`condition_${index}`}
                                checked={existingIndex !== -1}
                                aria-label={`condition_${index}`}
                                onChange={(event) => {
                                  const isSelected = event.target.checked;

                                  if (isSelected && existingIndex === -1) {
                                    handleAddCondition(conditionKey);
                                  } else if (!isSelected && existingIndex !== -1) {
                                    handleRemoveCondition(conditionKey);
                                  }
                                }}
                              />
                              <div>
                                <div>{condition.conditionName}</div>
                                <div>{condition.conditionDesc}</div>
                              </div>
                            </div>
                          )}) ?? <></>}
                      </div>
                    </div>
                  </>
                ) : (<></>)} */}

                {(request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS || request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS || request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS) ? (
                  <div style={{marginTop: '10px' }}>
                    {/* Active Reason Codes - shown for Modify Unavailable only */}
                    {request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS && activeReasonCodes?.length > 0 && (
                      <>
                        <h3 className="request-details-subheader">
                           Current Reason Codes
                          <ReasonCodeTooltip reasonCodes={allReasonCodes}/>
                        </h3>
                        <div className="reason-codes-readonly" style={{ marginBottom: '15px' }}>
                          {activeReasonCodes.map((rc, index) => (
                            <span key={index} className="reason-code-badge">
                              {rc.reasonCode}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                    <h3 className="request-details-subheader">
                      {request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS ? 'Requested Reason Codes' : 'Reason Codes'}
                      {request.requestTypeName !== REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS && <ReasonCodeTooltip reasonCodes={allReasonCodes}/>}
                    </h3>
                    {canAddReasonCodes ? (
                      <div>
                        <ReactSelect
                          name="reasonCodes"
                          className="pers-reason-codes"
                          options={reasonCodeOptions}
                          isMulti
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          value={addedReasonCodes?.map(rc => ({ value: rc.reasonId, label: rc.reasonCode })) ?? []}
                          onChange={(values) => handleAddReasonCodes(values)}
                          allowSelectAll={false}
                        />
                      </div>
                    ) : (
                      // Display selected reason codes as a read-only list if user cannot add/remove reason codes
                      <div className="reason-codes-readonly">
                        {addedReasonCodes.length ? (
                          addedReasonCodes.map((rc, index) => (
                            <span key={index} className="reason-code-badge">
                              {rc.reasonCode}
                            </span>
                          ))
                        ) : (
                          <div className="no-reason-codes">(none)</div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (<></>)}
              </>
            ) : (request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PHASE || request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PROJECT) && stagingChanges?.length ? (
              <div className="request-details-column">
                <h3 className="request-details-subheader">
                  Requested Changes
                </h3>
                <div className="request-change-list">
                  {stagingChanges.map((change, changeIndex) => {   
                    const fieldName = change.fieldName;
                    const className = change.def.type === "text"
                      ? "request-field-input"
                      : "field-value";
                      
                    // if this is styleComments:
                    // if the projectStyle change is being rejected, styleComments must also be rejected
                    // if the projectStyle change is being modified to "Other", styleComments must also be modified
                    const mustReject = fieldName === "styleComments" && lineItemApprovals.get("projectStyle") === LINE_ITEM_STATUS.REJECT;
                    const mustModify = fieldName === "styleComments" && lineItemApprovals.get("projectStyle") === LINE_ITEM_STATUS.MODIFY
                      && stagingChanges.find(fc => fc.fieldName === "projectStyle")?.newValue === "Other";

                    return (
                      <div key={changeIndex} className="request-change">
                        <div className="request-change-value field-name">
                          <span>{change.label}</span>
                        </div>
                        <div className="request-change-value old-value">
                          <b>Old Value:</b>
                          <span>{change.formatValue(change.oldValue) || "(none)"}</span>
                        </div>
                        <div className="request-change-value new-value">
                          <b>
                            New Value:
                          </b>
                          <EditableRequestField
                            className={className}
                            fieldDef={change.def}
                            fieldValue={change.newValue}
                            inputRef={
                              change.fieldName === "street" ? streetInputRef :
                              change.fieldName === "city" ? cityInputRef :
                              change.fieldName === "zip5" ? zip5InputRef :
                              undefined
                            }
                            onChanged={(modifiedValue, validationMessage) => handleValueModified(change.fieldName, modifiedValue, validationMessage, changeIndex)}
                            isEditing={editingItems.has(change.fieldName)}
                            onBlur={() => {}}
                            // onFocus={(validationMessage) => handleFocus(fieldName, validationMessage)}
                          />
                          {canReview ? (
                            statusName === "Action Taken" ? (
                              <div className="request-change-value request-change-approval">
                                <b>&nbsp;</b>
                                {editingItems.has(change.fieldName)
                                  ? <Link className="lnk-modify" disabled={mustModify} onClick={e => cancelFieldEditing(e, change.fieldName)}>Cancel</Link>
                                  : <Link className="lnk-modify" onClick={e => handleFieldEditing(e, change.fieldName)}>Modify</Link>
                                }
                              </div>
                            ) : statusName === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN ? (
                              <div className="request-change-value request-change-approval">
                                <Dropdown className="link-dropdown" onSelect={status => handleLineItemStatus(change.fieldName, status)}>
                                  <Dropdown.Toggle variant="link" disabled={mustReject || mustModify}>
                                    {lineItemApprovals.has(change.fieldName)
                                      ? lineItemApprovals.get(change.fieldName)
                                      : "Accept/Reject/Modify"
                                    }
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item eventKey={LINE_ITEM_STATUS.ACCEPT}>Accept</Dropdown.Item>
                                    <Dropdown.Item eventKey={LINE_ITEM_STATUS.REJECT}>Reject</Dropdown.Item>
                                    <Dropdown.Item eventKey={LINE_ITEM_STATUS.MODIFY}>Modify</Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            ) : (<></>)
                          ) : (<></>)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              ) : (<></>)}
          </div>

          {addedPhases?.length ? (
            <div className="request-details-section" style={{gap: 15}}>
              <div className="request-details-column">
                <h3 className="request-details-subheader">
                  Requested Phases
                </h3>
                <div className="request-change-list">
                  {addedPhases.map((phase, phaseIndex) => {   
                    return (
                      <div key={phaseIndex} className="request-change">
                        <div className="request-change-value field-name">
                          <span>{phase.phaseName || "(no phase name)"}</span>
                          {(statusName === REQUEST_STATUSES.ACTION_TAKEN || statusName === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN) 
                            && canReview ? (
                              <div className="request-change-value request-change-approval">
                                <Dropdown className="link-dropdown" onSelect={value => {
                                    if (phase.status === value) return;
                                    if (phase.status === LINE_ITEM_STATUS.MODIFY && value === LINE_ITEM_STATUS.ACCEPT) return;

                                    addedPhases[phaseIndex] = {
                                      ...originalAddedPhases[phaseIndex],
                                      status: value
                                    };
                                    setAddedPhases([...addedPhases]);

                                    phaseEditingItems.delete(phaseIndex);
                                    setPhaseEditingItems(new Map(phaseEditingItems));

                                    phaseValidationErrors.delete(phaseIndex);
                                    setPhaseValidationErrors(new Map(phaseValidationErrors));
                                  }}>
                                  <Dropdown.Toggle variant="link">
                                    {/* {phase.status} */}
                                    {!phase.status 
                                      ? "Accept/Reject"
                                      : phase.status === LINE_ITEM_STATUS.MODIFY
                                      ? "Accept"
                                      : phase.status}
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item eventKey={LINE_ITEM_STATUS.ACCEPT}>Accept</Dropdown.Item>
                                    <Dropdown.Item eventKey={LINE_ITEM_STATUS.REJECT}>Reject</Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            ) : (<></>)}
                        </div>
                        {["street", "buildingNumber", "unitNumber", "unitCount"]
                          .map(fieldName => {
                            const fieldDef = phaseFieldsMap.get(fieldName);
                            const className = fieldDef.type === "text"
                              ? "request-field-input"
                              : "field-value";
                            const canEdit = phase.status === LINE_ITEM_STATUS.ACCEPT || phase.status === LINE_ITEM_STATUS.MODIFY;
                            const isEditing = phaseEditingItems.get(phaseIndex)?.has(fieldName);

                            return (
                              <div key={`phase-${phaseIndex}-${fieldName}`} className="request-change-value new-value">
                                <b>{fieldDef.formatLabel()}: </b>
                                <EditableRequestField
                                  className={className}
                                  fieldDef={fieldDef}
                                  fieldValue={phase[fieldName]}
                                  inputRef={fieldName === "street" ? (ref => { if (ref) phaseStreetRefs.current[phaseIndex] = ref; }) : undefined}
                                  onChanged={(modifiedValue, validationMessage) => handlePhaseValueModified(phaseIndex, fieldName, modifiedValue, validationMessage)}
                                  isEditing={isEditing}
                                  onBlur={() => {}}
                                  // onFocus={(validationMessage) => handlePhaseFocus(i, fieldName, validationMessage)}
                                />
                              </div>
                            );
                          })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (<></>)}

          <div className="request-details-section">
            <div className="request-details-column">
              <h3 className="request-details-subheader">Attachments</h3>
              {canReview && statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED ? (
                <div className="requested-doc-select">
                  <label style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Requested Documents</label>
                  <ReactSelect
                    name="requestedDocuments"
                    className="pers-reason-codes"
                    options={availableDocuments.map(doc => ({ value: doc.documentRankingId, label: doc.documentName }))}
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    placeholder="Select documents..."
                    value={requestedDocuments.map(doc => ({ value: doc.documentRankingId, label: doc.documentName }))}
                    onChange={(values) => {
                      const selectedIds = new Set((values || []).map(v => v.value));
                      const newRequested = availableDocuments
                        .filter(doc => selectedIds.has(doc.documentRankingId))
                        .map(doc => ({ ...doc, documentName: doc.documentName ?? doc.name }));
                      setRequestedDocuments(newRequested);
                    }}
                    allowSelectAll={false}
                  />
                </div>
              ) : (<></>)}
              {documents?.map((docItem, i) => (
                <div key={i} className="request-doc-item">
                  <div className="request-doc-value">
                    <b>{docItem.documentName}: </b>
                    <a
                      className="pdf-link"
                      onClick={() => downloadFile(docItem.docMapId, docItem.s3ObjectName)}
                      style={{ 
                        cursor: "pointer", 
                        color: "#007bff", 
                        textDecoration: "underline" 
                      }}
                    >
                    {docItem.documentName}
                    </a>
                  </div>
                  <div className="request-doc-value comment-body">
                    <b>Note: </b>
                    {docItem.notes || "(none)"}
                  </div>
                </div>
              )) ?? <></>}
            </div>
          </div>
          
          {canReview && (
            <div className="request-details-section" style={{gap: 15}}>
              <div className="request-details-column" style={{gap: 0}}>
                <h3 className="request-details-subheader">
                  Notes
                  {isOtherRequested && (
                    <span style={{ color: 'red' }}>*</span>
                  )}
                </h3>
                <div className="form-group mt-3" style={{ position: "relative", paddingBottom: notesExternal === "both" ? "50px" : "0px" }}>
                  {/* <label htmlFor="notes">Notes</label> */}
                  <div
                    className={`notes-box${notesBoxActive ? " notes-box-active" : ""}`}
                    style={{ position: "relative", width: "95%", minWidth: 0, background: '#fff', borderRadius: '4px', display: 'flex', flexDirection: 'column' }}
                  >
                    <TextareaAutosize
                      id="notes"
                      minRows={3}
                      placeholder="Add notes here..."
                      value={note}
                      onChange={(e) => {
                        setNote(e.target.value);
                        setNotesBoxActive(true);
                      }}
                      onFocus={() => setNotesBoxActive(true)}
                      onBlur={() => setNotesBoxActive(false)}
                      style={{ width: "100%", marginTop: "5px", border: 'none', outline: 'none', resize: 'vertical', background: 'transparent', maxHeight: '120px', overflow: 'auto', flex: '1 1 auto' }}
                    />
                    <div className="toggle-container" style={{ marginTop: '8px', alignSelf: 'flex-end' }}>
                      <ToggleButtonGroup
                        color="primary"
                        value={notesExternal ? "both" : "internal"}
                        exclusive
                        onChange={(event, value) => {
                          setNotesExternal(value === "both");
                        }}
                        aria-label="text alignment"
                        size="small"
                      >
                        <ToggleButton value="internal" aria-label="left aligned">
                          Internal
                        </ToggleButton>
                        <ToggleButton value="both" aria-label="centered">
                          Internal & External
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                  </div>
                  {notesExternal && (
                    <div className="comment-note" style={{ marginTop: "8px", color: '#1176B5', fontWeight: 'bold', background: '#fff', padding: '4px 8px', borderRadius: '4px', width: '100%' }}>
                      This will allow lenders to view this note; use standardized language when possible.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      ) : (<></>)}
      <Modal.Footer className="modal-footer">
        {canReview ? (
          <>
            <Button className="cancel-button cancel-modal-button" onClick={handleClose}>
              Cancel
            </Button>
            <Button className="btn-submit" 
              style={{width: "unset"}} 
              disabled={!canSubmit || isSubmitting} 
              onClick={handleSubmit}>
              Submit
            </Button>
          </>
        ) : (
          <>
            <Button className="cancel-button cancel-modal-button" onClick={handleClose}>
              Close
            </Button>
            {(isLenderRole() || isTPORole()) &&
              !isReadonlyRole() &&
              isLenderActionableStatus(statusName) && (
                <Button
                  className="btn-submit"
                  style={{width: "unset"}}
                  href={`/lender-project-request/additional-documents/${request.projectId}/${request.requestId}`}>
                  Add Documents
                </Button>
              )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  ));
};

export default RequestDetailsModal;
